import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Task, UUID } from '@4zen/api-client';
import { api, type TaskFilters } from '@/lib/api';

export const taskKeys = {
  all: ['tasks'] as const,
  list: (filters: TaskFilters) => ['tasks', 'list', filters] as const,
  detail: (id: UUID) => ['tasks', 'detail', id] as const,
  calendar: (from: string, to: string, assignee?: UUID) =>
    ['tasks', 'calendar', from, to, assignee ?? 'me'] as const,
  history: (id: UUID) => ['tasks', 'history', id] as const,
};

function invalidateTaskViews(queryClient: ReturnType<typeof useQueryClient>) {
  void queryClient.invalidateQueries({ queryKey: taskKeys.all });
  void queryClient.invalidateQueries({ queryKey: ['smart-flow'] });
}

export function useTasks(filters: TaskFilters = {}) {
  return useQuery({
    queryKey: taskKeys.list(filters),
    queryFn: () => api.tasks.list(filters),
    placeholderData: (previous) => previous,
  });
}

export function useTask(id: UUID | undefined) {
  return useQuery({
    queryKey: taskKeys.detail(id!),
    queryFn: () => api.tasks.get(id!),
    enabled: Boolean(id),
  });
}

export function useCalendarTasks(from: string, to: string, assigneeId?: UUID) {
  return useQuery({
    queryKey: taskKeys.calendar(from, to, assigneeId),
    queryFn: () => api.tasks.calendar({ from, to, assignee_id: assigneeId }),
  });
}

export function useTaskHistory(id: UUID | undefined) {
  return useQuery({
    queryKey: taskKeys.history(id!),
    queryFn: () => api.tasks.history(id!),
    enabled: Boolean(id),
  });
}

export function useCreateTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => api.tasks.create(body),
    onSuccess: () => invalidateTaskViews(queryClient),
  });
}

export function useUpdateTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: UUID; body: Record<string, unknown> }) =>
      api.tasks.update(id, body),
    onSuccess: (task) => {
      queryClient.setQueryData(taskKeys.detail(task.id), task);
      invalidateTaskViews(queryClient);
    },
  });
}

export function useCompleteTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, note, minutes }: { id: UUID; note?: string; minutes?: number }) =>
      api.tasks.complete(id, note, minutes),
    onSuccess: () => invalidateTaskViews(queryClient),
  });
}

export function useMarkNotDone() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: UUID; reason: string }) => api.tasks.markNotDone(id, reason),
    onSuccess: () => invalidateTaskViews(queryClient),
  });
}

/**
 * Drag-and-drop moves land here. The calendar is updated optimistically so the
 * card does not jump back to its old slot while the request is in flight.
 */
export function useRescheduleTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      to_date,
      to_start,
      reason,
    }: {
      id: UUID;
      to_date: string;
      to_start?: string;
      reason?: string;
    }) => api.tasks.reschedule(id, { to_date, to_start, reason }),
    onMutate: async ({ id, to_date, to_start }) => {
      await queryClient.cancelQueries({ queryKey: ['tasks', 'calendar'] });
      const snapshots = queryClient.getQueriesData<Task[]>({ queryKey: ['tasks', 'calendar'] });
      for (const [key, tasks] of snapshots) {
        if (!tasks) continue;
        queryClient.setQueryData(
          key,
          tasks.map((task) =>
            task.id === id
              ? { ...task, scheduled_for: to_date, scheduled_start: to_start ?? null }
              : task,
          ),
        );
      }
      return { snapshots };
    },
    onError: (_error, _variables, context) => {
      for (const [key, tasks] of context?.snapshots ?? []) {
        queryClient.setQueryData(key, tasks);
      }
    },
    onSettled: () => invalidateTaskViews(queryClient),
  });
}

export function useToggleChecklistItem(taskId: UUID) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId, isDone }: { itemId: UUID; isDone: boolean }) =>
      api.tasks.toggleChecklistItem(taskId, itemId, isDone),
    onSuccess: (task) => queryClient.setQueryData(taskKeys.detail(taskId), task),
  });
}

export function useAddChecklistItem(taskId: UUID) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (title: string) => api.tasks.addChecklistItem(taskId, { title }),
    onSuccess: (task) => queryClient.setQueryData(taskKeys.detail(taskId), task),
  });
}
